/**
 * Blank tile letter picker (UX-SPEC §5): a compact A–Z grid anchored next to
 * the square. Type a letter or click one; arrow keys move between letters.
 *
 * - Escape cancels: a blank that was just placed goes back to the rack; a
 *   placed blank being re-lettered ("change") keeps its letter.
 * - A click elsewhere just closes it (the tile stays; Play re-opens the picker
 *   if the blank still has no letter).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { LETTERS, cellName } from '@wordloom/engine';
import { sfx } from '../audio/sfx';
import { useDraftStore, type BlankPickerState } from '../state/draftStore';
import { useUiStore } from '../state/uiStore';
import { IconClose, IconRecall } from './Icons';
import { trapTab } from './Modal';
import './LetterPicker.css';

/** Keys per row as laid out: 7, or 6 on narrow phones (LetterPicker.css), so arrows follow the grid. */
function columns(grid: Element | null | undefined): number {
  const n = grid ? getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length : 0;
  return n > 1 ? n : 7;
}
const GAP = 10;
const MARGIN = 8;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function LetterPicker() {
  const picker = useDraftStore((s) => s.picker);
  return createPortal(
    <AnimatePresence>
      {picker && <PickerPanel key={`${picker.tileId}:${picker.mode}`} picker={picker} />}
    </AnimatePresence>,
    document.body,
  );
}

function cellEl(row: number, col: number): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-cell="${row},${col}"]`);
}

function PickerPanel({ picker }: { picker: BlankPickerState }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const keys = useRef<(HTMLButtonElement | null)[]>([]);
  const current = useDraftStore((s) => s.placements.find((p) => p.tileId === picker.tileId)?.letter ?? '');
  const [pos, setPos] = useState<{ left: number; top: number; side: 'above' | 'below' } | null>(null);
  const square = cellName(picker.row, picker.col);
  const titleId = `picker-title-${picker.tileId}`;

  // Anchor next to the square: below when there is room, else above; clamped to the viewport.
  const position = useCallback(() => {
    const anchor = cellEl(picker.row, picker.col);
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fitsBelow = a.bottom + GAP + h <= vh - MARGIN;
    const fitsAbove = a.top - GAP - h >= MARGIN;
    const side = fitsBelow || !fitsAbove ? 'below' : 'above';
    const top = clamp(side === 'below' ? a.bottom + GAP : a.top - GAP - h, MARGIN, Math.max(MARGIN, vh - h - MARGIN));
    const left = clamp(a.left + a.width / 2 - w / 2, MARGIN, Math.max(MARGIN, vw - w - MARGIN));
    setPos({ left, top, side });
  }, [picker.row, picker.col]);

  useLayoutEffect(() => {
    position();
  }, [position]);

  useEffect(() => {
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [position]);

  const close = useCallback(() => useDraftStore.getState().setPicker(null), []);

  const cancel = useCallback(() => {
    const draft = useDraftStore.getState();
    const placement = draft.placements.find((p) => p.tileId === picker.tileId);
    if (picker.mode === 'place' && placement && !placement.letter) {
      draft.recall(picker.tileId);
      sfx.return();
    }
    draft.setPicker(null);
  }, [picker.mode, picker.tileId]);

  const choose = (letter: string) => {
    const draft = useDraftStore.getState();
    draft.setBlankLetter(picker.tileId, letter);
    draft.setPicker(null);
    sfx.place();
    useUiStore.getState().announce(`Blank on ${square} is now ${letter}.`);
  };

  const returnToRack = () => {
    const draft = useDraftStore.getState();
    draft.recall(picker.tileId);
    draft.setPicker(null);
    sfx.return();
  };

  // Focus the current letter (or A) once the panel has been positioned. Until
  // then it is `visibility: hidden`, and focus() on a hidden element is a
  // silent no-op, which would leave typed letters going to the board instead.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (!pos || focusedOnce.current) return;
    focusedOnce.current = true;
    const index = current ? LETTERS.indexOf(current) : 0;
    keys.current[Math.max(0, index)]?.focus({ preventScroll: true });
    // Only the first placement matters: later letter changes must not steal focus.
  }, [pos, current]);

  // Give focus back to the square when the picker closes.
  useEffect(() => {
    const panel = panelRef.current;
    return () => {
      const active = document.activeElement;
      if (!active || active === document.body || panel?.contains(active)) {
        cellEl(picker.row, picker.col)?.focus({ preventScroll: true });
      }
    };
    // Mount-only on purpose: the square is fixed for this panel's lifetime.
  }, []);

  // A pointer press anywhere else closes the picker (the tile stays placed).
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [close]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // The game's global shortcuts must not see keys typed into the picker.
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === 'Tab') {
      trapTab(e, panelRef.current);
      return;
    }
    if (e.key.length === 1 && /[a-z]/i.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      choose(e.key.toUpperCase());
      return;
    }
    const index = keys.current.findIndex((k) => k === document.activeElement);
    if (index < 0) return;
    const cols = columns(keys.current[0]?.parentElement);
    const delta =
      e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? cols : e.key === 'ArrowUp' ? -cols : 0;
    if (!delta) return;
    e.preventDefault();
    const next = clamp(index + delta, 0, LETTERS.length - 1);
    keys.current[next]?.focus();
  };

  const offset = pos?.side === 'above' ? 8 : -8;

  return (
    <motion.div
      ref={panelRef}
      className={clsx('picker', pos && `picker--${pos.side}`)}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
      initial={{ opacity: 0, scale: 0.94, y: offset }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.12 } }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
      onKeyDown={onKeyDown}
    >
      <div className="picker__head">
        <div className="picker__titles">
          <h2 id={titleId} className="picker__title">
            {picker.mode === 'change' ? 'Change letter' : 'Choose a letter'}
          </h2>
          <span className="picker__sub">Blank tile on {square} · worth 0</span>
        </div>
        <button type="button" className="picker__close" aria-label="Cancel" onClick={cancel}>
          <IconClose />
        </button>
      </div>
      <div className="picker__grid" role="group" aria-label="Letters">
        {LETTERS.map((letter, i) => (
          <button
            key={letter}
            ref={(el) => {
              keys.current[i] = el;
            }}
            type="button"
            className={clsx('picker__key', current === letter && 'is-current')}
            aria-pressed={current === letter}
            onClick={() => choose(letter)}
          >
            {letter}
          </button>
        ))}
      </div>
      <div className="picker__foot">
        {picker.mode === 'change' ? (
          <button type="button" className="btn btn--ghost btn--sm picker__return" onClick={returnToRack}>
            <IconRecall /> Return to rack
          </button>
        ) : (
          <>
            <span className="picker__hint picker__hint--keys">
              Type a letter · <kbd className="kbd">Esc</kbd> returns the tile
            </span>
            <span className="picker__hint picker__hint--touch">Tap the letter this blank stands for</span>
          </>
        )}
      </div>
    </motion.div>
  );
}
